import React, { useState, useEffect } from 'react';
import api from '../services/apiService';
import ReactMarkdown from 'react-markdown';
import {
  Calendar,
  Target,
  TrendingUp,
  Download,
  Loader,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Play,
  List,
  Edit,
  Trash2,
  Save,
  X,
  Plus
} from 'lucide-react';
import './PlanningTab.css';

// Helper component to render rationale in both markdown and JSON formats
const RationaleDisplay = ({ rationale }) => {
  if (!rationale) return null;

  // New unified format: plain markdown string
  if (typeof rationale === 'string') {
    return (
      <div className="rationale-panel">
        <h3>
          <CheckCircle size={20} />
          Why This Plan Works
        </h3>
        <div className="rationale-markdown">
          <ReactMarkdown>{rationale}</ReactMarkdown>
        </div>
      </div>
    );
  }

  // Check if it's the object markdown format
  if (rationale.format === 'markdown' && rationale.content) {
    return (
      <div className="rationale-panel">
        <h3>
          <CheckCircle size={20} />
          Why This Plan Works
        </h3>
        <div className="rationale-markdown">
          <ReactMarkdown>{rationale.content}</ReactMarkdown>
        </div>
      </div>
    );
  }

  // Legacy JSON format
  return (
    <div className="rationale-panel">
      <h3>
        <CheckCircle size={20} />
        Why This Plan Works
      </h3>
      <div className="rationale-sections">
        {rationale.personalized_for_you && rationale.personalized_for_you.length > 0 && (
          <div className="rationale-section">
            <h4>✓ Personalized for You</h4>
            <ul>
              {rationale.personalized_for_you.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {rationale.science_based_progression && rationale.science_based_progression.length > 0 && (
          <div className="rationale-section">
            <h4>✓ Science-Based Progression</h4>
            <ul>
              {rationale.science_based_progression.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {rationale.balanced_recovery && rationale.balanced_recovery.length > 0 && (
          <div className="rationale-section">
            <h4>✓ Balanced Recovery</h4>
            <ul>
              {rationale.balanced_recovery.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {rationale.goal_aligned && rationale.goal_aligned.length > 0 && (
          <div className="rationale-section">
            <h4>✓ Goal-Aligned</h4>
            <ul>
              {rationale.goal_aligned.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {rationale.key_success_factors && rationale.key_success_factors.length > 0 && (
          <div className="rationale-section">
            <h4>✓ Key Success Factors</h4>
            <ul>
              {rationale.key_success_factors.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const PlanningTab = () => {
  // State management
  const [view, setView] = useState('wizard'); // 'wizard', 'schedule', 'schedules', 'schedule-detail', 'profile'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Wizard state
  const [goal, setGoal] = useState('');
  const [useProfile, setUseProfile] = useState(true);

  // Schedule state
  const [schedule, setSchedule] = useState(null);
  const [optimizations, setOptimizations] = useState([]);
  const [rationale, setRationale] = useState(null);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [expandedRec, setExpandedRec] = useState(null);

  // Schedules list state
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  // Editing state
  const [editingSession, setEditingSession] = useState(null); // { weekNum, sessionIdx }
  const [editedSessionData, setEditedSessionData] = useState(null);
  const [renamingScheduleId, setRenamingScheduleId] = useState(null);
  const [newScheduleName, setNewScheduleName] = useState('');
  const [updateRequest, setUpdateRequest] = useState('');
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  // Profile state
  const [profile, setProfile] = useState(null);

  // Load profile on mount
  useEffect(() => {
    if (view === 'profile') {
      loadProfile();
    } else if (view === 'schedules') {
      loadSchedules();
    }
  }, [view]);

  const loadProfile = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/api/planning/skater-profile`);
      setProfile(response.data.profile);
    } catch (err) {
      console.error('Error loading profile:', err);
      setError(err.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadSchedules = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/api/planning/schedules`);
      setSchedules(response.data.schedules);
    } catch (err) {
      console.error('Error loading schedules:', err);
      setError(err.response?.data?.detail || 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const loadScheduleDetail = async (scheduleId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/api/planning/schedules/${scheduleId}`);
      setSelectedSchedule(response.data.schedule);
      setView('schedule-detail');
    } catch (err) {
      console.error('Error loading schedule:', err);
      setError(err.response?.data?.detail || 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const deleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;

    try {
      await api.delete(`/api/planning/schedules/${scheduleId}`);
      loadSchedules(); // Reload list
    } catch (err) {
      console.error('Error deleting schedule:', err);
      setError(err.response?.data?.detail || 'Failed to delete schedule');
    }
  };

  const renameSchedule = async (scheduleId, newName) => {
    try {
      await api.put(`/api/planning/schedules/${scheduleId}/rename`, { name: newName });
      setRenamingScheduleId(null);
      setNewScheduleName('');
      loadSchedules(); // Reload list

      // Update selected schedule if it's the one being viewed
      if (selectedSchedule && selectedSchedule.id === scheduleId) {
        setSelectedSchedule({ ...selectedSchedule, name: newName });
      }
    } catch (err) {
      console.error('Error renaming schedule:', err);
      setError(err.response?.data?.detail || 'Failed to rename schedule');
    }
  };

  const updateScheduleFromInput = async () => {
    if (!updateRequest.trim() || !selectedSchedule) return;

    setLoading(true);
    setError(null);

    try {
      // Use extended timeout for AI schedule updates (can take 60+ seconds)
      const response = await api.post(
        `/api/planning/schedules/${selectedSchedule.id}/update`,
        { update_request: updateRequest },
        { timeout: 120000 }
      );

      setSelectedSchedule(response.data.schedule);
      setUpdateRequest('');
      setShowUpdateModal(false);
    } catch (err) {
      console.error('Error updating schedule:', err);
      setError(err.response?.data?.detail || 'Failed to update schedule');
    } finally {
      setLoading(false);
    }
  };

  const saveSessionEdit = async (weekNum, sessionIdx) => {
    if (!selectedSchedule || !editedSessionData) return;

    try {
      // Update the schedule data
      const updatedSchedule = { ...selectedSchedule };
      updatedSchedule.weeks[weekNum - 1].sessions[sessionIdx] = editedSessionData;

      // Save to backend
      await api.put(`/api/planning/schedules/${selectedSchedule.id}`, updatedSchedule);

      // Update local state
      setSelectedSchedule(updatedSchedule);
      setEditingSession(null);
      setEditedSessionData(null);
    } catch (err) {
      console.error('Error saving session edit:', err);
      setError(err.response?.data?.detail || 'Failed to save session');
    }
  };

  const saveSessionEditForCurrentSchedule = (weekNum, sessionIdx) => {
    if (!schedule || !editedSessionData) return;

    try {
      // Update the current schedule in local state
      const updatedSchedule = { ...schedule };
      updatedSchedule.weeks[weekNum - 1].sessions[sessionIdx] = editedSessionData;

      // Update local state
      setSchedule(updatedSchedule);
      setEditingSession(null);
      setEditedSessionData(null);
    } catch (err) {
      console.error('Error saving session edit:', err);
      setError('Failed to save session');
    }
  };

  const cancelSessionEdit = () => {
    setEditingSession(null);
    setEditedSessionData(null);
  };

  const startSessionEdit = (weekNum, sessionIdx, session) => {
    setEditingSession({ weekNum, sessionIdx });
    setEditedSessionData({ ...session });
  };

  const generateSchedule = async () => {
    if (!goal.trim()) {
      setError('Please describe your training request');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use extended timeout for AI schedule generation (can take 60+ seconds)
      const response = await api.post(`/api/planning/generate-schedule`, {
        user_input: goal,
        include_profile: useProfile
      }, { timeout: 120000 });

      setSchedule(response.data.schedule);
      setRationale(response.data.schedule.rationale || null);

      // Automatically get optimization recommendations
      getOptimizations(response.data.schedule);

      // Switch to schedule view
      setView('schedule');
    } catch (err) {
      console.error('Error generating schedule:', err);
      setError(err.response?.data?.detail || 'Failed to generate schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getOptimizations = async (scheduleData) => {
    try {
      // Use extended timeout for AI optimization (can take 60+ seconds)
      const response = await api.post(`/api/planning/optimize`, {
        schedule_data: scheduleData,
        include_profile: true
      }, { timeout: 120000 });

      setOptimizations(response.data.recommendations);
    } catch (err) {
      console.error('Error getting optimizations:', err);
      // Don't show error to user - optimizations are optional
    }
  };

  const downloadMarkdown = async () => {
    // Use selectedSchedule if viewing a saved schedule, otherwise use schedule
    const scheduleToExport = selectedSchedule || schedule;
    if (!scheduleToExport) return;

    setLoading(true);

    try {
      const response = await api.post(`/api/planning/export-markdown`, scheduleToExport);

      // Create downloadable file
      const blob = new Blob([response.data.markdown], { type: 'text/markdown' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `training-schedule-${new Date().toISOString().split('T')[0]}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting markdown:', err);
      setError(err.response?.data?.detail || 'Failed to export schedule');
    } finally {
      setLoading(false);
    }
  };

  const downloadCalendar = async () => {
    // Use selectedSchedule if viewing a saved schedule, otherwise use schedule
    const scheduleToExport = selectedSchedule || schedule;
    if (!scheduleToExport) return;

    setLoading(true);

    try {
      const response = await api.post(`/api/planning/export-ical`, scheduleToExport);

      // Create downloadable .ics file
      const blob = new Blob([response.data.ical], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename || `training-schedule-${new Date().toISOString().split('T')[0]}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting calendar:', err);
      setError(err.response?.data?.detail || 'Failed to export calendar');
    } finally {
      setLoading(false);
    }
  };

  const getIntensityColor = (level) => {
    // Normalize intensity level to handle both old and new formats
    const normalized = level?.toLowerCase();

    // Map intensity levels to color scheme: peak=purple, high=red, moderate=yellow, low=green, recovery=light green
    const colors = {
      very_hard: '#9333ea',  // Peak - Purple
      max: '#9333ea',        // Peak (alternate name) - Purple
      peak: '#9333ea',       // Peak (alternate name) - Purple
      hard: '#ef4444',       // High - Red
      high: '#ef4444',       // High (alternate name) - Red
      moderate: '#eab308',   // Moderate - Yellow
      light: '#22c55e',      // Low - Green
      low: '#22c55e',        // Low (alternate name) - Green
      rest: '#86efac',       // Recovery - Light Green
      recovery: '#86efac'    // Recovery (alternate name) - Light Green
    };
    return colors[normalized] || '#9ca3af';
  };

  const getIntensityLabel = (level) => {
    const normalized = level?.toLowerCase();

    const labels = {
      very_hard: 'Peak',
      max: 'Peak',
      peak: 'Peak',
      hard: 'High',
      high: 'High',
      moderate: 'Moderate',
      light: 'Low',
      low: 'Low',
      rest: 'Recovery',
      recovery: 'Recovery'
    };
    return labels[normalized] || level?.replace('_', ' ') || '';
  };

  // Parse date string as local date (not UTC) to avoid timezone offset issues
  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const formatSessionDate = (dateString) => {
    const date = parseLocalDate(dateString);
    if (!date) return '';
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  const getPriorityIcon = (priority) => {
    if (priority === 'high') return <AlertCircle size={16} color="#ef4444" />;
    if (priority === 'medium') return <AlertCircle size={16} color="#f59e0b" />;
    return <AlertCircle size={16} color="#60a5fa" />;
  };

  // Render functions for different views
  const renderWizard = () => (
    <div className="planning-wizard">
      <div className="wizard-header">
        <Target size={32} />
        <h2>Generate Training Schedule</h2>
        <p>Create an AI-powered training plan based on your goals and timeline</p>
      </div>

      <div className="wizard-form">
        <div className="form-group">
          <label>Training Request</label>
          <textarea
            placeholder="Describe your training needs in natural language. Examples:

• 1-week plan to master my 3F-3T combination starting November 9, 2025
• Help me prepare for Nationals on December 15, 2025. Starting Monday with 1 week to work on under-rotation issues.
• One-week recovery plan after ankle injury. Light training only, starting Nov 9.
• Competition prep starting Nov 24: fix my 3Lz under-rotation and improve landing stability

Include any of:
- Training goal
- Start/end dates or duration
- Competition dates
- Available training hours
- Current skill level
- Problem areas
- Specific elements to work on"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            className="textarea-field training-request-input"
            rows={15}
          />
          <span className="input-hint">Type naturally - no special format required. The AI will understand your request.</span>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={useProfile}
              onChange={(e) => setUseProfile(e.target.checked)}
            />
            <span>Include my training history and context</span>
          </label>
          <span className="input-hint">Uses your saved progress, goals, and notes to personalize the plan</span>
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <button
          onClick={generateSchedule}
          disabled={loading}
          className="generate-button"
        >
          {loading ? (
            <>
              <Loader className="spinner" size={18} />
              Generating Schedule...
            </>
          ) : (
            <>
              <Play size={18} />
              Generate Schedule
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderSchedule = () => {
    if (!schedule) return null;

    return (
      <div className="schedule-view">
        {/* Header */}
        <div className="schedule-header">
          <div className="schedule-title">
            <Calendar size={24} />
            <div>
              <h2>{schedule.title}</h2>
              <p>{schedule.start_date} to {schedule.end_date}</p>
            </div>
          </div>
          <div className="schedule-actions">
            <button onClick={downloadCalendar} className="action-button">
              <Calendar size={16} />
              Export to Calendar
            </button>
            <button onClick={downloadMarkdown} className="action-button">
              <Download size={16} />
              Export Markdown
            </button>
            <button onClick={() => setView('wizard')} className="action-button secondary">
              New Schedule
            </button>
          </div>
        </div>

        {/* Plan Rationale Panel */}
        <RationaleDisplay rationale={rationale} />

        {/* Weekly Schedule - Unified Calendar Card View */}
        <div className="weekly-schedule">
          <h3>Weekly Breakdown</h3>
          {schedule.weeks.map((week) => (
            <div key={week.week_number} className="week-card">
              <div
                className="week-header"
                onClick={() => setExpandedWeek(expandedWeek === week.week_number ? null : week.week_number)}
              >
                <div className="week-info">
                  <h4>Week {week.week_number}: {week.goal}</h4>
                  <span className="week-meta">
                    {week.start_date} - {week.end_date} | {week.phase} | {week.weekly_load} min
                  </span>
                </div>
                {expandedWeek === week.week_number ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>

              {expandedWeek === week.week_number && (
                <div className="week-sessions">
                  {week.sessions.map((session, idx) => {
                    const isEditing = editingSession?.weekNum === week.week_number && editingSession?.sessionIdx === idx;

                    return (
                      <div key={idx} className="session-card">
                        <div
                          className="session-intensity-bar"
                          style={{ backgroundColor: getIntensityColor(session.intensity_level) }}
                        />

                        {isEditing ? (
                          // Edit Mode
                          <div className="session-edit-form">
                            <div className="form-row">
                              <div className="form-group">
                                <label>Duration (minutes)</label>
                                <input
                                  type="number"
                                  value={editedSessionData.duration_minutes}
                                  onChange={(e) => setEditedSessionData({
                                    ...editedSessionData,
                                    duration_minutes: parseInt(e.target.value)
                                  })}
                                  className="input-field"
                                />
                              </div>
                              <div className="form-group">
                                <label>Intensity</label>
                                <select
                                  value={editedSessionData.intensity_level}
                                  onChange={(e) => setEditedSessionData({
                                    ...editedSessionData,
                                    intensity_level: e.target.value
                                  })}
                                  className="input-field"
                                >
                                  <option value="light">Low</option>
                                  <option value="moderate">Moderate</option>
                                  <option value="hard">High</option>
                                  <option value="very_hard">Peak</option>
                                  <option value="rest">Recovery</option>
                                </select>
                              </div>
                            </div>

                            <div className="form-group">
                              <label>Focus Areas (comma-separated)</label>
                              <textarea
                                value={editedSessionData.focus_areas.join(', ')}
                                onChange={(e) => setEditedSessionData({
                                  ...editedSessionData,
                                  focus_areas: e.target.value.split(',').map(s => s.trim())
                                })}
                                className="textarea-field"
                                rows={3}
                              />
                            </div>

                            <div className="form-group">
                              <label>Drills</label>
                              {editedSessionData.drills && editedSessionData.drills.map((drill, drillIdx) => (
                                <div key={drillIdx} className="drill-edit-row">
                                  <textarea
                                    value={drill}
                                    onChange={(e) => {
                                      const newDrills = [...editedSessionData.drills];
                                      newDrills[drillIdx] = e.target.value;
                                      setEditedSessionData({
                                        ...editedSessionData,
                                        drills: newDrills
                                      });
                                    }}
                                    className="textarea-field"
                                    rows={2}
                                  />
                                  <button
                                    onClick={() => {
                                      const newDrills = editedSessionData.drills.filter((_, i) => i !== drillIdx);
                                      setEditedSessionData({
                                        ...editedSessionData,
                                        drills: newDrills
                                      });
                                    }}
                                    className="icon-button danger"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              ))}
                              <button
                                onClick={() => setEditedSessionData({
                                  ...editedSessionData,
                                  drills: [...(editedSessionData.drills || []), '']
                                })}
                                className="action-button secondary"
                              >
                                <Plus size={16} />
                                Add Drill
                              </button>
                            </div>

                            <div className="form-group">
                              <label>Notes</label>
                              <textarea
                                value={editedSessionData.notes || ''}
                                onChange={(e) => setEditedSessionData({
                                  ...editedSessionData,
                                  notes: e.target.value
                                })}
                                className="textarea-field"
                                rows={2}
                              />
                            </div>

                            <div className="edit-actions">
                              <button
                                onClick={cancelSessionEdit}
                                className="action-button secondary"
                              >
                                <X size={16} />
                                Cancel
                              </button>
                              <button
                                onClick={() => saveSessionEditForCurrentSchedule(week.week_number, idx)}
                                className="action-button"
                              >
                                <Save size={16} />
                                Save Changes
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div className="session-content">
                            <div className="session-header-row">
                              <span className="session-date">
                                {formatSessionDate(session.date)}
                              </span>
                              <span className={`session-intensity intensity-${session.intensity_level}`}>
                                {getIntensityLabel(session.intensity_level)}
                              </span>
                              <button
                                onClick={() => startSessionEdit(week.week_number, idx, session)}
                                className="icon-button"
                              >
                                <Edit size={16} />
                              </button>
                            </div>
                            <div className="session-type">{session.session_type} - {session.duration_minutes} min</div>
                            <div className="session-focus">
                              <strong>Focus:</strong> {session.focus_areas.join(', ')}
                            </div>
                            {session.drills && session.drills.length > 0 && (
                              <div className="session-drills">
                                <strong>Drills:</strong>
                                <ul>
                                  {session.drills.map((drill, i) => (
                                    <li key={i}><ReactMarkdown>{drill}</ReactMarkdown></li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {/* Fallback: Show raw content if drills couldn't be parsed */}
                            {(!session.drills || session.drills.length === 0) && session.raw_content && (
                              <div className="session-raw-content">
                                <ReactMarkdown>{session.raw_content}</ReactMarkdown>
                              </div>
                            )}
                            {session.notes && (
                              <div className="session-notes">
                                <ReactMarkdown>{session.notes}</ReactMarkdown>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderProfile = () => {
    if (!profile) {
      if (loading) {
        return (
          <div className="loading-state">
            <Loader className="spinner" size={32} />
            <p>Loading profile...</p>
          </div>
        );
      }
      return null;
    }

    // Check if we have any structured data beyond basic info
    const hasStructuredData =
      (profile.current_goals?.length > 0) ||
      (profile.recent_progress?.length > 0) ||
      (profile.health_status?.active_injuries?.length > 0) ||
      (profile.technical_capabilities?.recent_improvements?.length > 0) ||
      (profile.competition_schedule?.length > 0);

    return (
      <div className="profile-view">
        <h2>Your Training Profile</h2>

        {/* Basic Info Section - always show if available */}
        {profile.basic_info && (profile.basic_info.name || profile.basic_info.level) && (
          <div className="profile-section">
            <h3>Basic Information</h3>
            {profile.basic_info.name && (
              <div className="profile-item">
                <strong>Name:</strong> {profile.basic_info.name}
              </div>
            )}
            {profile.basic_info.level && (
              <div className="profile-item">
                <strong>Skill Level:</strong> {profile.basic_info.level}
              </div>
            )}
          </div>
        )}

        {hasStructuredData ? (
          <>
            {/* Structured Data Sections */}
            {profile.current_goals?.length > 0 && (
              <div className="profile-section">
                <h3>Current Goals ({profile.current_goals.length})</h3>
                {profile.current_goals.map((goal, idx) => (
                  <div key={idx} className="profile-item">
                    <span className={`priority-badge priority-${goal.priority}`}>{goal.priority}</span>
                    <span>{goal.description}</span>
                  </div>
                ))}
              </div>
            )}

            {profile.recent_progress?.length > 0 && (
              <div className="profile-section">
                <h3>Recent Progress</h3>
                {profile.recent_progress.slice(0, 5).map((item, idx) => (
                  <div key={idx} className="profile-item">
                    <span className="date-badge">{item.date}</span>
                    <span>{item.summary}</span>
                  </div>
                ))}
              </div>
            )}

            {(profile.health_status?.active_injuries?.length > 0 || profile.health_status?.recovery_status) && (
              <div className="profile-section">
                <h3>Health Status</h3>
                <div className="health-status">
                  <div>
                    <strong>Active Injuries:</strong> {profile.health_status?.active_injuries?.length || 0}
                  </div>
                  <div>
                    <strong>Recovery Status:</strong> {profile.health_status?.recovery_status || 'Normal'}
                  </div>
                </div>
              </div>
            )}

            {profile.competition_schedule?.length > 0 && (
              <div className="profile-section">
                <h3>Competition Schedule</h3>
                {profile.competition_schedule.map((comp, idx) => (
                  <div key={idx} className="profile-item">
                    <span className={`priority-badge priority-${comp.status === 'upcoming' ? 'high' : 'low'}`}>
                      {comp.status}
                    </span>
                    <span>{comp.name}: {comp.description}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* Show raw context when no structured data is available */
          profile.raw_context && profile.raw_context.trim() && (
            <div className="profile-section">
              <h3>Your Context</h3>
              <div className="raw-context-display">
                <ReactMarkdown>{profile.raw_context}</ReactMarkdown>
              </div>
            </div>
          )
        )}

        {/* Show empty state if no data at all */}
        {!hasStructuredData && !profile.raw_context?.trim() && !profile.basic_info?.name && (
          <div className="empty-state">
            <TrendingUp size={48} />
            <h3>No profile data yet</h3>
            <p>Start chatting with Lumi to build your training profile. Your goals, progress, and health status will appear here as you interact.</p>
          </div>
        )}
      </div>
    );
  };

  const renderSchedulesList = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <Loader className="spinner" size={32} />
          <p>Loading schedules...</p>
        </div>
      );
    }

    return (
      <div className="schedules-list-view">
        <div className="schedules-header">
          <h2>Your Saved Schedules ({schedules.length})</h2>
          <button onClick={() => setView('wizard')} className="action-button">
            <Plus size={16} />
            Create New Schedule
          </button>
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {schedules.length === 0 ? (
          <div className="empty-state">
            <Calendar size={48} />
            <h3>No schedules yet</h3>
            <p>Create your first training schedule to get started</p>
            <button onClick={() => setView('wizard')} className="generate-button">
              <Plus size={18} />
              Generate Schedule
            </button>
          </div>
        ) : (
          <div className="schedules-grid">
            {schedules.map((sched) => (
              <div key={sched.id} className="schedule-card">
                <div className="schedule-card-header">
                  {renamingScheduleId === sched.id ? (
                    <div className="rename-input-group">
                      <input
                        type="text"
                        value={newScheduleName}
                        onChange={(e) => setNewScheduleName(e.target.value)}
                        className="input-field"
                        autoFocus
                      />
                      <button
                        onClick={() => renameSchedule(sched.id, newScheduleName)}
                        className="icon-button"
                      >
                        <Save size={16} />
                      </button>
                      <button
                        onClick={() => {
                          setRenamingScheduleId(null);
                          setNewScheduleName('');
                        }}
                        className="icon-button"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <h3>{sched.name}</h3>
                  )}
                </div>
                <div className="schedule-card-meta">
                  <p><strong>Goal:</strong> {sched.primary_goal}</p>
                  <p><strong>Period:</strong> {sched.start_date} to {sched.end_date}</p>
                  {sched.competition_date && (
                    <p><strong>Competition:</strong> {sched.competition_date}</p>
                  )}
                </div>
                <div className="schedule-card-actions">
                  <button
                    onClick={() => loadScheduleDetail(sched.id)}
                    className="action-button"
                  >
                    <Calendar size={16} />
                    View Schedule
                  </button>
                  <button
                    onClick={() => {
                      setRenamingScheduleId(sched.id);
                      setNewScheduleName(sched.name);
                    }}
                    className="action-button secondary"
                  >
                    <Edit size={16} />
                    Rename
                  </button>
                  <button
                    onClick={() => deleteSchedule(sched.id)}
                    className="action-button danger"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderScheduleDetail = () => {
    if (!selectedSchedule) return null;

    return (
      <div className="schedule-view">
        {/* Header */}
        <div className="schedule-header">
          <div className="schedule-title">
            <button onClick={() => setView('schedules')} className="back-button">
              ← Back to Schedules
            </button>
            <div>
              <h2>{selectedSchedule.name}</h2>
              <p>{selectedSchedule.start_date} to {selectedSchedule.end_date}</p>
            </div>
          </div>
          <div className="schedule-actions">
            <button
              onClick={() => setShowUpdateModal(true)}
              className="action-button"
            >
              <Edit size={16} />
              <span className="btn-text">Update</span>
            </button>
            <button onClick={downloadCalendar} className="action-button">
              <Calendar size={16} />
              <span className="btn-text">Calendar</span>
            </button>
            <button onClick={downloadMarkdown} className="action-button">
              <Download size={16} />
              <span className="btn-text">Export</span>
            </button>
          </div>
        </div>

        {/* Goal Section */}
        {selectedSchedule.primary_goal && (
          <div className="schedule-goal">
            <h3>
              <Target size={20} />
              Goal
            </h3>
            <p>{selectedSchedule.primary_goal}</p>
          </div>
        )}

        {/* Update Modal */}
        {showUpdateModal && (
          <div className="modal-overlay" onClick={() => setShowUpdateModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Update Training Schedule</h3>
                <button onClick={() => setShowUpdateModal(false)} className="close-button">
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Current Goal</label>
                  <input
                    type="text"
                    value={selectedSchedule.primary_goal}
                    disabled
                    className="input-field"
                  />
                </div>
                <div className="form-group">
                  <label>What changes would you like to make?</label>
                  <textarea
                    value={updateRequest}
                    onChange={(e) => setUpdateRequest(e.target.value)}
                    placeholder="e.g., Add more recovery days in week 2. Reduce intensity in week 3."
                    className="textarea-field"
                    rows={4}
                  />
                  <span className="input-hint">Describe your changes in plain English</span>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  onClick={() => setShowUpdateModal(false)}
                  className="action-button secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={updateScheduleFromInput}
                  disabled={loading || !updateRequest.trim()}
                  className="action-button"
                >
                  {loading ? (
                    <>
                      <Loader className="spinner" size={16} />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Update Schedule
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Plan Rationale Panel */}
        <RationaleDisplay rationale={selectedSchedule.rationale} />

        {/* Weekly Schedule - Unified Calendar Card View with Inline Editing */}
        <div className="weekly-schedule">
          <h3>Weekly Breakdown</h3>
          {selectedSchedule.weeks.map((week) => (
          <div key={week.week_number} className="week-card">
            <div
              className="week-header"
              onClick={() => setExpandedWeek(expandedWeek === week.week_number ? null : week.week_number)}
            >
              <div className="week-info">
                <h4>Week {week.week_number}: {week.goal}</h4>
                <span className="week-meta">
                  {week.start_date} - {week.end_date} | {week.phase} | {week.weekly_load} min
                </span>
              </div>
              {expandedWeek === week.week_number ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>

            {expandedWeek === week.week_number && (
              <div className="week-sessions">
                {week.sessions.map((session, idx) => {
                  const isEditing = editingSession?.weekNum === week.week_number && editingSession?.sessionIdx === idx;

                  return (
                    <div key={idx} className="session-card">
                      <div
                        className="session-intensity-bar"
                        style={{ backgroundColor: getIntensityColor(session.intensity_level) }}
                      />

                      {isEditing ? (
                        // Edit Mode
                        <div className="session-edit-form">
                          <div className="form-row">
                            <div className="form-group">
                              <label>Duration (minutes)</label>
                              <input
                                type="number"
                                value={editedSessionData.duration_minutes}
                                onChange={(e) => setEditedSessionData({
                                  ...editedSessionData,
                                  duration_minutes: parseInt(e.target.value)
                                })}
                                className="input-field"
                              />
                            </div>
                            <div className="form-group">
                              <label>Intensity</label>
                              <select
                                value={editedSessionData.intensity_level}
                                onChange={(e) => setEditedSessionData({
                                  ...editedSessionData,
                                  intensity_level: e.target.value
                                })}
                                className="input-field"
                              >
                                <option value="light">Low</option>
                                <option value="moderate">Moderate</option>
                                <option value="hard">High</option>
                                <option value="very_hard">Peak</option>
                                <option value="rest">Recovery</option>
                              </select>
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Focus Areas (comma-separated)</label>
                            <textarea
                              value={editedSessionData.focus_areas.join(', ')}
                              onChange={(e) => setEditedSessionData({
                                ...editedSessionData,
                                focus_areas: e.target.value.split(',').map(s => s.trim())
                              })}
                              className="textarea-field"
                              rows={3}
                            />
                          </div>

                          <div className="form-group">
                            <label>Drills</label>
                            {editedSessionData.drills && editedSessionData.drills.map((drill, drillIdx) => (
                              <div key={drillIdx} className="drill-edit-row">
                                <textarea
                                  value={drill}
                                  onChange={(e) => {
                                    const newDrills = [...editedSessionData.drills];
                                    newDrills[drillIdx] = e.target.value;
                                    setEditedSessionData({
                                      ...editedSessionData,
                                      drills: newDrills
                                    });
                                  }}
                                  className="textarea-field"
                                  rows={2}
                                />
                                <button
                                  onClick={() => {
                                    const newDrills = editedSessionData.drills.filter((_, i) => i !== drillIdx);
                                    setEditedSessionData({
                                      ...editedSessionData,
                                      drills: newDrills
                                    });
                                  }}
                                  className="icon-button danger"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => setEditedSessionData({
                                ...editedSessionData,
                                drills: [...(editedSessionData.drills || []), '']
                              })}
                              className="action-button secondary"
                            >
                              <Plus size={16} />
                              Add Drill
                            </button>
                          </div>

                          <div className="form-group">
                            <label>Notes</label>
                            <textarea
                              value={editedSessionData.notes || ''}
                              onChange={(e) => setEditedSessionData({
                                ...editedSessionData,
                                notes: e.target.value
                              })}
                              className="textarea-field"
                              rows={2}
                            />
                          </div>

                          <div className="edit-actions">
                            <button
                              onClick={cancelSessionEdit}
                              className="action-button secondary"
                            >
                              <X size={16} />
                              Cancel
                            </button>
                            <button
                              onClick={() => saveSessionEdit(week.week_number, idx)}
                              className="action-button"
                            >
                              <Save size={16} />
                              Save Changes
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="session-content">
                          <div className="session-header-row">
                            <span className="session-date">
                              {formatSessionDate(session.date)}
                            </span>
                            <span className={`session-intensity intensity-${session.intensity_level}`}>
                              {getIntensityLabel(session.intensity_level)}
                            </span>
                            <button
                              onClick={() => startSessionEdit(week.week_number, idx, session)}
                              className="icon-button"
                            >
                              <Edit size={16} />
                            </button>
                          </div>
                          <div className="session-type">{session.session_type} - {session.duration_minutes} min</div>
                          <div className="session-focus">
                            <strong>Focus:</strong> {session.focus_areas.join(', ')}
                          </div>
                          {session.drills && session.drills.length > 0 && (
                            <div className="session-drills">
                              <strong>Drills:</strong>
                              <ul>
                                {session.drills.map((drill, i) => (
                                  <li key={i}><ReactMarkdown>{drill}</ReactMarkdown></li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {/* Fallback: Show raw content if drills couldn't be parsed */}
                          {(!session.drills || session.drills.length === 0) && session.raw_content && (
                            <div className="session-raw-content">
                              <ReactMarkdown>{session.raw_content}</ReactMarkdown>
                            </div>
                          )}
                          {session.notes && (
                            <div className="session-notes">
                              <ReactMarkdown>{session.notes}</ReactMarkdown>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        </div>
      </div>
    );
  };

  return (
    <div className="planning-tab">
      <div className="planning-nav">
        <button
          className={`nav-btn ${view === 'wizard' ? 'active' : ''}`}
          onClick={() => setView('wizard')}
        >
          <Target size={18} />
          Generate Schedule
        </button>
        <button
          className={`nav-btn ${view === 'schedules' || view === 'schedule-detail' ? 'active' : ''}`}
          onClick={() => setView('schedules')}
        >
          <List size={18} />
          View Schedules
        </button>
        {schedule && (
          <button
            className={`nav-btn ${view === 'schedule' ? 'active' : ''}`}
            onClick={() => setView('schedule')}
          >
            <Calendar size={18} />
            Current Schedule
          </button>
        )}
        <button
          className={`nav-btn ${view === 'profile' ? 'active' : ''}`}
          onClick={() => setView('profile')}
        >
          <TrendingUp size={18} />
          My Profile
        </button>
      </div>

      <div className="planning-content">
        {view === 'wizard' && renderWizard()}
        {view === 'schedule' && renderSchedule()}
        {view === 'schedules' && renderSchedulesList()}
        {view === 'schedule-detail' && renderScheduleDetail()}
        {view === 'profile' && renderProfile()}
      </div>
    </div>
  );
};

export default PlanningTab;
